import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StoreGroupManager = ({ clientId, group, allStores, onClose, onUpdate }) => {
  const [memberStores, setMemberStores] = useState([]);
  const [nonMemberStores, setNonMemberStores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    // Categorize stores into members and non-members
    const memberIds = new Set(group.store_ids || []);
    const members = [];
    const nonMembers = [];

    allStores.forEach(store => {
      if (memberIds.has(store.id)) {
        members.push(store);
      } else {
        nonMembers.push(store);
      }
    });

    // Sort alphabetically
    members.sort((a, b) => a.name.localeCompare(b.name));
    nonMembers.sort((a, b) => a.name.localeCompare(b.name));

    setMemberStores(members);
    setNonMemberStores(nonMembers);
  }, [group, allStores]);

  const handleAddStores = async () => {
    if (selectedToAdd.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/store-groups/${group.id}/stores`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ store_ids: selectedToAdd })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to add stores');
      }

      // Update local state
      const addedIds = new Set(data.added);
      const newMembers = nonMemberStores.filter(s => addedIds.has(s.id));
      
      setMemberStores(prev => [...prev, ...newMembers].sort((a, b) => a.name.localeCompare(b.name)));
      setNonMemberStores(prev => prev.filter(s => !addedIds.has(s.id)));
      setSelectedToAdd([]);
      
      setSuccessMessage(`Added ${data.added.length} store(s) to group`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStore = async (storeId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/store-groups/${group.id}/stores/${storeId}`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to remove store');
      }

      if (data.removed) {
        // Update local state
        const removedStore = memberStores.find(s => s.id === storeId);
        if (removedStore) {
          setMemberStores(prev => prev.filter(s => s.id !== storeId));
          setNonMemberStores(prev => [...prev, removedStore].sort((a, b) => a.name.localeCompare(b.name)));
        }
        
        setSuccessMessage('Store removed from group');
        setTimeout(() => setSuccessMessage(null), 3000);
        
        onUpdate();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleStoreSelection = (storeId) => {
    setSelectedToAdd(prev => 
      prev.includes(storeId)
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
  };

  const filteredNonMembers = nonMemberStores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col"
        data-testid="store-group-manager-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manage Group: {group.name}</h2>
            <p className="text-sm text-gray-500">Add or remove stores from this group</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="close-group-manager"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 text-red-600 text-sm rounded" data-testid="group-manager-error">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mx-6 mt-4 p-3 bg-green-50 text-green-600 text-sm rounded" data-testid="group-manager-success">
            {successMessage}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Current Members */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <h3 className="font-medium text-gray-900">
                Current Members ({memberStores.length})
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {memberStores.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No stores in this group yet</p>
              ) : (
                <div className="space-y-2">
                  {memberStores.map(store => (
                    <div
                      key={store.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100"
                      data-testid={`member-store-${store.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{store.name}</p>
                        <p className="text-xs text-gray-500 truncate">{store.id}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveStore(store.id)}
                        disabled={loading}
                        className="ml-2 p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                        title="Remove from group"
                        data-testid={`remove-store-${store.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: Add Stores */}
          <div className="w-1/2 flex flex-col">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <h3 className="font-medium text-gray-900">
                Available Stores ({nonMemberStores.length})
              </h3>
            </div>
            
            {/* Search */}
            <div className="px-4 py-2 border-b border-gray-200 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search stores..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="search-available-stores"
                />
              </div>
            </div>

            {/* Store List */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredNonMembers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  {searchTerm ? 'No stores match your search' : 'All stores are already members'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredNonMembers.map(store => (
                    <label
                      key={store.id}
                      className={`flex items-center p-2 rounded cursor-pointer ${
                        selectedToAdd.includes(store.id) ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid={`available-store-${store.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedToAdd.includes(store.id)}
                        onChange={() => toggleStoreSelection(store.id)}
                        className="mr-3"
                        data-testid={`checkbox-store-${store.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{store.name}</p>
                        <p className="text-xs text-gray-500 truncate">{store.id}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Add Button */}
            {selectedToAdd.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={handleAddStores}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  data-testid="add-selected-stores"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add {selectedToAdd.length} Store(s)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            data-testid="done-group-manager"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreGroupManager;
